/**
 * ProofJourneyBar.jsx — GRANULLO_INSTITUTIONAL_DEMO_PATH_V1
 *
 * Persistent floating proof navigator during institutional demo mode.
 * Reads demo state from sessionStorage. Minimal, editorial, non-intrusive.
 * Feels: boardroom-grade proof progression, not a product tour.
 *
 * Includes proof checkpoint badges that reinforce MONEY ≠ MISUSE at key steps.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowLeft, X, Check } from 'lucide-react';
import { analytics } from '../../lib/analytics';
import { chronometry } from '../../lib/chronometry';

const EDITORIAL_RED = '#CD2200';

const PROOF_STEPS = [
  { path: '/', label: 'Theorem', brief: 'The structural premise', checkpoint: 'Why money needs structural governance' },
  { path: '/pricing', label: 'Pricing', brief: 'Economic inevitability', checkpoint: 'Transparent unit economics — no hidden costs' },
  { path: '/estimator', label: 'Estimator', brief: 'Misuse prevention proof', checkpoint: 'Quantified ROI for your exact program type' },
  { path: '/partners', label: 'Certification', brief: 'Integration confidence', checkpoint: 'Zero-query partner certification harness' },
  { path: '/signals', label: 'Signals', brief: 'Trust intelligence', checkpoint: 'Real-time trust observatory — every anomaly visible' },
  { path: '/playbook', label: 'Playbook', brief: 'Trust routing lanes', checkpoint: 'The fastest path from evaluation to production' },
  { path: '/ask-intelligence', label: 'Intelligence', brief: 'Ask anything', checkpoint: 'AI that explains governance, not just answers' },
  { path: '/data-fabric', label: 'Data Fabric', brief: 'Provider federation', checkpoint: 'Multi-provider trust fabric without vendor lock-in' },
  { path: '/granullo-atlas', label: 'Atlas', brief: 'Forward vision', checkpoint: 'Global commitment network and labs sandbox' },
];

export const PERSONA_EMPHASIS = {
  cio: {
    label: 'Bank CIO',
    emphases: [
      'Settlement integrity proof',
      'Security-first pricing model',
      'Quantified misuse downside',
      'Sandbox certification standard',
      'Anomaly detection confidence',
      'Governance routing methodology',
      'Evidence-based intelligence',
      'Multi-provider trust fabric',
      'Global commitment network',
    ],
  },
  cto: {
    label: 'Fintech CTO',
    emphases: [
      'API-first architecture',
      'Transparent cost structure',
      'Economics-driven risk model',
      'Automated certification pipeline',
      'Real-time signal processing',
      'Structured trust playbook',
      'Programmatic intelligence API',
      'Adapter federation protocol',
      'Multi-provider data fabric',
    ],
  },
  cfo: {
    label: 'CFO / Treasury',
    emphases: [
      'Revenue protection thesis',
      'Unit economics clarity',
      'Quantified ROI estimation',
      'Compliance certification cost',
      'Signal-driven risk visibility',
      'Trust routing efficiency',
      'Decision intelligence ROI',
      'Provider coverage economics',
      'Global exposure mapping',
    ],
  },
  regulator: {
    label: 'Regulator / Auditor',
    emphases: [
      'Evidence chain integrity',
      'Transparent fee structure',
      'Quantified compliance impact',
      'Verifiable certification audit',
      'Anomaly audit trail',
      'Governance methodology audit',
      'Explainable intelligence',
      'Regulatory-grade integration',
      'Jurisdictional risk mapping',
    ],
  },
  procurement: {
    label: 'Enterprise Procurement',
    emphases: [
      'Vendor assessment framework',
      'Total cost of ownership',
      'Misuse prevention value case',
      'Integration certification proof',
      'Signal coverage assessment',
      'Trust routing capabilities',
      'Intelligence capabilities matrix',
      'Self-serve onboarding path',
      'Platform scale evidence',
    ],
  },
  csr: {
    label: 'CSR / Impact Lead',
    emphases: [
      'Circularity enforcement proof',
      'Impact-aligned pricing',
      'Beneficiary reach quantification',
      'ESG certification pathway',
      'Impact signal observatory',
      'CSR program playbook',
      'Impact intelligence queries',
      'Multi-stakeholder data fabric',
      'Global impact commitment map',
    ],
  },
  cbdc: {
    label: 'CBDC / Subsidy Program',
    emphases: [
      'Programmable money governance',
      'Government-scale pricing',
      'Disbursement leakage estimation',
      'Digital currency certification',
      'Welfare signal monitoring',
      'CBDC deployment playbook',
      'Policy intelligence layer',
      'Central bank data federation',
      'Sovereign money infrastructure',
    ],
  },
};

export function ProofJourneyBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [demo, setDemo] = useState(null);
  const [showCheckpoint, setShowCheckpoint] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('granullo_demo');
      if (raw) {
        const parsed = JSON.parse(raw);
        setDemo(parsed);
        // Initialize chronometry on first detection
        if (!chronometry.getSession() && parsed.persona) {
          chronometry.initSession(parsed.persona);
        }
        // Record step entry for chronometry
        const currentStep = PROOF_STEPS[parsed.step];
        if (currentStep) {
          chronometry.recordStepEntry(parsed.step, currentStep.label);
        }
        // Show checkpoint briefly on each step change
        if (parsed.step > 0) {
          setShowCheckpoint(true);
          const t = setTimeout(() => setShowCheckpoint(false), 3000);
          return () => clearTimeout(t);
        }
      } else {
        setDemo(null);
      }
    } catch { setDemo(null); }
  }, [location.pathname]);

  if (!demo) return null;

  const { persona, step, visited = [] } = demo;
  const personaData = PERSONA_EMPHASIS[persona] || PERSONA_EMPHASIS.cio;
  const currentStep = PROOF_STEPS[step] || PROOF_STEPS[0];
  const emphasis = personaData.emphases[step] || '';

  const goTo = (nextStep) => {
    if (nextStep < 0 || nextStep >= PROOF_STEPS.length) return;
    const newVisited = [...new Set([...visited, step])];
    const next = { ...demo, step: nextStep, visited: newVisited };
    sessionStorage.setItem('granullo_demo', JSON.stringify(next));
    setDemo(next);
    analytics.track('demo.step_navigate', { persona, from: step, to: nextStep, stepLabel: PROOF_STEPS[nextStep].label });
    navigate(PROOF_STEPS[nextStep].path);
  };

  const exit = () => {
    const completed = step === PROOF_STEPS.length - 1;
    chronometry.endSession(completed);
    sessionStorage.removeItem('granullo_demo');
    analytics.track('demo.exit', { persona, exitStep: step, visitedCount: visited.length + 1, completedAll: completed });
    setDemo(null);
  };

  const allVisited = visited.length >= PROOF_STEPS.length - 1;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-4 pointer-events-none" data-testid="proof-journey-bar">
      {/* Proof checkpoint toast */}
      {showCheckpoint && currentStep.checkpoint && (
        <div className="max-w-[500px] mx-auto mb-2 pointer-events-auto">
          <div
            className="rounded-lg px-4 py-2 flex items-center gap-2 text-[11px]"
            style={{ background: `${EDITORIAL_RED}10`, border: `1px solid ${EDITORIAL_RED}20`, color: EDITORIAL_RED }}
            data-testid="proof-checkpoint-toast"
          >
            <Check size={10} />
            <span style={{ fontFamily: '"Inter Tight", sans-serif' }}>{currentStep.checkpoint}</span>
          </div>
        </div>
      )}

      <div
        className="max-w-[900px] mx-auto rounded-2xl px-5 py-3 pointer-events-auto flex items-center justify-between gap-4"
        style={{
          background: 'var(--g-bg)',
          border: '1px solid var(--g-border)',
          boxShadow: '0 -4px 40px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Left: Step info */}
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-[11px] font-mono font-semibold shrink-0" style={{ color: EDITORIAL_RED }}>
            {String(step + 1).padStart(2, '0')}/{String(PROOF_STEPS.length).padStart(2, '0')}
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate" style={{ fontFamily: '"Inter Tight", sans-serif', color: 'var(--g-text)' }}>
              {currentStep.label}
              <span className="text-[10px] font-normal ml-2" style={{ color: 'var(--g-text-secondary)' }}>
                {personaData.label}
              </span>
            </div>
            <div className="text-[11px] truncate" style={{ color: 'var(--g-text-secondary)' }}>{emphasis}</div>
          </div>
        </div>

        {/* Center: Progress dots */}
        <div className="hidden sm:flex items-center gap-1.5">
          {PROOF_STEPS.map((_, i) => {
            const isVisited = visited.includes(i) || i === step;
            return (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="w-2 h-2 rounded-full transition-all duration-200"
                style={{
                  background: i === step ? EDITORIAL_RED : isVisited ? `${EDITORIAL_RED}60` : 'var(--g-border)',
                  transform: i === step ? 'scale(1.4)' : 'scale(1)',
                }}
                data-testid={`proof-dot-${i}`}
              />
            );
          })}
        </div>

        {/* Right: Navigation controls */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => goTo(step - 1)}
            disabled={step === 0}
            className="p-1.5 rounded-lg transition-all"
            style={{ opacity: step === 0 ? 0.3 : 1, color: 'var(--g-text-secondary)' }}
            data-testid="proof-prev"
          >
            <ArrowLeft size={14} />
          </button>
          <button
            onClick={() => step === PROOF_STEPS.length - 1 ? exit() : goTo(step + 1)}
            className="px-3 py-1 rounded-lg text-[11px] font-medium transition-all flex items-center gap-1"
            style={{
              background: allVisited && step === PROOF_STEPS.length - 1 ? `${EDITORIAL_RED}25` : `${EDITORIAL_RED}15`,
              color: EDITORIAL_RED,
              border: `1px solid ${EDITORIAL_RED}30`,
            }}
            data-testid="proof-next"
          >
            {step === PROOF_STEPS.length - 1 ? (allVisited ? 'Journey Complete' : 'Complete') : 'Next'}
            <ArrowRight size={11} />
          </button>
          <button
            onClick={exit}
            className="p-1 rounded-lg ml-1"
            style={{ color: 'var(--g-text-secondary)' }}
            data-testid="proof-exit"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

export { PROOF_STEPS };
